/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { PageContainer } from '../components/Containers/PageContainer';
import { ChatSidebar } from '../components/Chatbot/ChatSidebar';
import { ChatPanel } from '../components/Chatbot/ChatPanel';
import { apiUrl } from '../utilities/apiUrl';

// Formats an ISO timestamp the way the mock sessions used to read (e.g. "Aug 14").
const formatSessionDate = (isoString) =>
  new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

export const ChatbotPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [chatKey, setChatKey] = useState(0);

  const loadSessions = () => {
    fetch(apiUrl('/assistant/conversations'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        const items = data?.items ?? [];
        setSessions(
          items.map((item) => ({
            id: item.id,
            title: item.title,
            date: formatSessionDate(item.created_at),
          }))
        );
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleNewChat = () => {
    setActiveSessionId(null);
    setChatKey((key) => key + 1);
  };

  const handleSelectSession = (session) => {
    setActiveSessionId(session.id);
    setChatKey((key) => key + 1);
  };

  // Handles chat history reload on first response of assistant
  const handleConversationStarted = (id) => {
    setActiveSessionId(id);
    loadSessions();
  };

  const handleDeleteSession = async (id) => {
    setSessions((prev) => prev.filter((session) => session.id !== id));
    if (activeSessionId === id) handleNewChat();

    try {
      const response = await fetch(apiUrl(`/assistant/conversations/${id}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok && response.status !== 404) {
        throw new Error(`Failed to delete conversation: ${response.status}`);
      }
    } catch (err) {
      console.log(err);
      loadSessions();
    }
  };

  // Handles renaming a session from the sidebar's menu: persist it, update the list, and remount
  const handleRenameSession = async (id, title) => {
    const previousTitle = sessions.find((session) => session.id === id)?.title;

    setSessions((prev) =>
      prev.map((session) =>
        session.id === id ? { ...session, title } : session
      )
    );
    if (id === activeSessionId) setChatKey((key) => key + 1);

    try {
      const response = await fetch(apiUrl(`/assistant/conversations/${id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (!response.ok)
        throw new Error(`Failed to rename conversation: ${response.status}`);
    } catch (err) {
      console.log(err);
      if (previousTitle !== undefined) {
        setSessions((prev) =>
          prev.map((session) =>
            session.id === id ? { ...session, title: previousTitle } : session
          )
        );
      }
    }
  };

  // Handles renaming a session from the chat panel itself, keeping the sidebar in sync.
  const handleConversationRenamed = (id, title) => {
    setSessions((prev) =>
      prev.map((session) =>
        session.id === id ? { ...session, title } : session
      )
    );
  };

  const activeTitle =
    sessions.find((session) => session.id === activeSessionId)?.title ??
    'New Chat';

  return (
    <PageContainer selectedPage={6}>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 'calc(100vh - 70px)',
          overflow: 'hidden',
        }}
      >
        <ChatSidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen((o) => !o)}
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSelectSession={handleSelectSession}
          onNewChat={handleNewChat}
          onRenameSession={handleRenameSession}
          onDeleteSession={handleDeleteSession}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', p: 3 }}>
          <ChatPanel
            key={chatKey}
            variant="dedicated"
            initialTitle={activeTitle}
            conversationId={activeSessionId}
            onNewChat={handleNewChat}
            onConversationStarted={handleConversationStarted}
            onConversationRenamed={handleConversationRenamed}
          />
        </Box>
      </Box>
    </PageContainer>
  );
};
